import React from 'react';
import './Legal.css';

function PrivacyPolicy() {
  return (
    <div className="legal-container">
      <div className="legal-content">
        <div className="legal-header">
          <h1>Warrior Walkup Music</h1>
          <h2>Privacy Notice</h2>
          <p className="legal-meta-small">V2</p>
          <p className="legal-meta">Nerdette Ventures LLC | Effective Date: February 27, 2026</p>
        </div>

        <section>
          <h3>1. Introduction</h3>
          <p>Warrior Walkup Music ("we," "our," or "us") is operated by Nerdette Ventures LLC, an Illinois limited liability company. We care about privacy and want you to be familiar with how we collect, use, and share ("Process") information that identifies you, relates to you, describes you, or could be linked, directly or indirectly, to you as an individual or, if you are a California resident, your household ("Personal Information").</p>
          <p>This Privacy Notice describes our practices concerning information we collect through our web application and mobile application (collectively, the "App") when used by coaches, team managers, and parents in connection with sports team walkup music management.</p>
          <p>By using Warrior Walkup Music, you agree to the collection and use of information in accordance with this Privacy Notice. If you do not agree, please do not use the App.</p>
        </section>

        <section>
          <h3>2. Personal Information Categories</h3>
          <p>We collect Personal Information from a variety of sources and use and disclose it for multiple purposes. This Personal Information falls into the categories described below.</p>

          <h4>2.1 Contact Information</h4>
          <p><strong>2.1.1 Examples.</strong> Name, email address, and telephone number.</p>
          <p><strong>2.1.2 Sources.</strong> We collect this data directly from you when you create an account or complete your profile in the App. We may also receive your name and email from Google if you choose to sign in with Google OAuth.</p>
          <p><strong>2.1.3 Uses.</strong> We use this information to create and manage your account, send transactional emails (such as account confirmations), respond to support inquiries, and enable you to use the App's features.</p>
          <p><strong>2.1.4 Disclosure.</strong> We may share this information with service providers or as permitted by law as described in Section 3 below.</p>

          <h4>2.2 Account Credentials and Authentication Data</h4>
          <p><strong>2.2.1 Examples.</strong> Encrypted password, Google OAuth token, Spotify OAuth access token, Spotify user ID, and Spotify display name.</p>
          <p><strong>2.2.2 Sources.</strong> We collect this data when you register for an account or connect your Spotify or Google account to the App.</p>
          <p><strong>2.2.3 Uses.</strong> We use this information to authenticate your identity, enable Spotify music search and playback within the App, and maintain your session.</p>
          <p><strong>2.2.4 Disclosure.</strong> OAuth tokens are transmitted directly to Spotify and Google in accordance with their respective privacy policies. We do not share your credentials with any other third parties.</p>

          <h4>2.3 User-Generated Content</h4>
          <p><strong>2.3.1 Examples.</strong> Playlist names, player names, player jersey numbers, assigned songs, clip start times, clip duration settings, and AI-generated clip analysis results.</p>
          <p><strong>2.3.2 Sources.</strong> We collect this data when you create playlists and assign walkup music to players within the App.</p>
          <p><strong>2.3.3 Uses.</strong> We use this information to provide the core functionality of the App, including displaying and playing walkup music during games. Song metadata (not personal data) may be sent to Anthropic's Claude AI to identify energetic clip segments when you use the AI clip selection feature.</p>
          <p><strong>2.3.4 Disclosure.</strong> When you share a playlist with other users, their assigned player names and song information will be visible to those you grant access. You control who has access to your playlists. Song metadata (artist, title, duration) may be shared with Anthropic solely for AI clip analysis; no personal information is transmitted.</p>

          <h4>2.4 Unique Personal Identifiers</h4>
          <p><strong>2.4.1 Examples.</strong> Firebase user ID, device identifiers, Internet Protocol (IP) addresses, and session identifiers.</p>
          <p><strong>2.4.2 Sources.</strong> We collect this data automatically when you use the App through Firebase Authentication and Firebase Analytics.</p>
          <p><strong>2.4.3 Uses.</strong> We use this information to maintain your session, analyze usage patterns, understand how you use the App across devices, and ensure security.</p>
          <p><strong>2.4.4 Disclosure.</strong> We may share this information with service providers or as permitted by law as described in Section 3 below.</p>

          <h4>2.5 Geolocation Data</h4>
          <p><strong>2.5.1 Examples.</strong> Approximate geographic location derived from IP address.</p>
          <p><strong>2.5.2 Sources.</strong> Firebase Analytics collects approximate location data automatically when you use the App.</p>
          <p><strong>2.5.3 Uses.</strong> We use this information to analyze usage of the App and ensure we are compliant in our presentation of content across jurisdictions.</p>
          <p><strong>2.5.4 Disclosure.</strong> We may share this information with service providers or as permitted by law as described in Section 3 below.</p>

          <h4>2.6 Cookies and Similar Technologies</h4>
          <p>We use cookies and similar tracking technologies to operate the App. These include:</p>
          <ul>
            <li>Strictly necessary cookies — required for authentication and core App functionality</li>
            <li>Analytical/performance cookies — to understand how users interact with the App (via Firebase Analytics)</li>
            <li>Functionality cookies — to remember your preferences and settings</li>
          </ul>
          <p>You can control cookies through your browser settings. Disabling certain cookies may affect the functionality of the App. For more information about cookies, visit <a href="http://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer">allaboutcookies.org</a>.</p>

          <h4>2.7 Firebase Analytics</h4>
          <p>We use Google Firebase Analytics to understand how users interact with the App. Firebase Analytics collects usage data including session duration, features used, device type, and approximate location. If you wish to opt out of Firebase Analytics data collection, you may do so through your device's privacy settings.</p>

          <h4>2.8 All Categories</h4>
          <p>We may also use data from any of the categories above in connection with data analysis, fraud monitoring and prevention, internal quality assurance, developing new features, enhancing the App, compliance with legal requirements, enforcement of these Terms, protection of the App and its users, and operating and expanding our business activities. We may share or disclose data from any category in response to requests from public and government authorities; to protect the rights, privacy, safety, or property of Nerdette Ventures LLC; and to pursue available remedies or limit damages we may sustain.</p>
        </section>

        <section>
          <h3>3. Information Disclosure</h3>
          <p>We do not sell your Personal Information. We may disclose your Personal Information to the entities and under the circumstances described below.</p>

          <h4>3.1 Service Providers</h4>
          <p>We disclose your Personal Information to third-party service providers who assist us in operating the App, including:</p>
          <ul>
            <li>Google Firebase — authentication, database (Firestore), and analytics</li>
            <li>Spotify AB — music search, playback, and OAuth account authentication</li>
            <li>Anthropic — AI-powered clip selection (song metadata only; no personal data transmitted)</li>
            <li>Vercel — web application hosting</li>
            <li>Render — backend server hosting</li>
          </ul>
          <p>Each of these providers maintains their own privacy policies governing the use of your information.</p>

          <h4>3.2 Playlist Sharing</h4>
          <p>When you share a playlist with other users, those users will be able to see player names, assigned songs, and clip settings as permitted by the access level you grant. You are solely responsible for managing who has access to your playlists. We are not responsible for any use of your playlist data by users you have authorized.</p>

          <h4>3.3 Merger or Acquisition</h4>
          <p>We may disclose your Personal Information to a third party who acquires any part of Nerdette Ventures LLC's business, whether by merger, consolidation, divestiture, or purchase of all or a substantial portion of our assets. In such an event, we will provide notice through the App prior to your Personal Information becoming subject to a different privacy notice.</p>

          <h4>3.4 Disclosure Permitted by Law</h4>
          <p>We may disclose your Personal Information if required or permitted to do so by law or legal process; to respond to claims; or to protect the rights, interests, privacy, property, or safety of Nerdette Ventures LLC, you, or third parties.</p>
        </section>

        <section>
          <h3>4. Information Safeguards</h3>
          <p>We have implemented reasonable and appropriate physical, technical, and administrative measures to help protect your Personal Information from loss, misuse, unauthorized access or disclosure, alteration, or destruction. These include Firebase security rules, encrypted data transmission (HTTPS), and secure OAuth token handling. Unfortunately, no security system is one hundred percent secure, and we cannot guarantee the security of all information you provide to us via the App.</p>
        </section>

        <section>
          <h3>5. Other Important Information</h3>

          <h4>5.1 Third-Party Links and Services</h4>
          <p>The App integrates with third-party services including Spotify and Google. We do not control these services and are not responsible for their privacy practices, policies, or security measures. We encourage you to read the privacy policies of every third-party service you connect to through the App. Your use of Spotify is governed by Spotify's Privacy Policy available at <a href="https://www.spotify.com/privacy" target="_blank" rel="noopener noreferrer">spotify.com/privacy</a>.</p>

          <h4>5.2 Privacy Notice Changes</h4>
          <p>We may update this Privacy Notice from time to time. We will make updated and date-stamped versions available in the App. The revised notice will take effect upon posting, but new provisions will only apply to data collected after the posting is effective. Should we need to materially change the way we Process information previously collected about you, we will only do so after notifying you.</p>

          <h4>5.3 Minors</h4>
          <p>Warrior Walkup Music is not directed to children under the age of 12. We do not knowingly Process the information of children under 12 unless we verify through reasonable efforts that a parent or guardian has consented to such processing.</p>
          <p><strong>Note:</strong> Player names and jersey numbers entered into the App may belong to minors. This information is entered solely by coaches, team managers or guardians (children under 12 should not be creating their own accounts or entering in their own data) and is used exclusively for managing walkup music assignments. We do not use this information for any other purpose, and we do not display it publicly.</p>

          <h4>5.4 Data Transfers</h4>
          <p>Nerdette Ventures LLC is based in Illinois, USA. Your data may be stored on servers operated by our service providers in various jurisdictions. By using the App, you consent to the transfer of your information to the United States and other jurisdictions where our service providers operate. We will use appropriate safeguards governing the transfer and usage of your Personal Information.</p>

          <h4>5.5 Retention Period</h4>
          <p>We will retain your Personal Information for as long as your account is active or as needed to provide the App's services. We will retain and use your information as necessary to comply with legal obligations, resolve disputes, and enforce our agreements. You may request deletion of your account and associated data at any time by contacting us at <a href="mailto:support@warriorwalkupmusic.com">support@warriorwalkupmusic.com</a>.</p>

          <h4>5.6 Aggregated Data</h4>
          <p>We may aggregate or anonymize your Personal Information so that it no longer identifies you individually and use such aggregated data to develop, improve, and optimize the App. Subject to applicable laws, we may provide aggregated, non-identifying information to third parties.</p>
        </section>

        <section>
          <h3>6. Rights Under the General Data Protection Regulation (GDPR)</h3>
          <p>If you are located in the European Union or United Kingdom, you have the following rights regarding your Personal Information:</p>

          <h4>6.1 Withdrawing Consent</h4>
          <p>If you have provided consent for us to Process your Personal Information, you may withdraw it at any time by contacting us at <a href="mailto:support@warriorwalkupmusic.com">support@warriorwalkupmusic.com</a>.</p>

          <h4>6.2 Access / Right to Know</h4>
          <p>You may request access to the Personal Information we hold about you. Requests must be submitted in writing to <a href="mailto:support@warriorwalkupmusic.com">support@warriorwalkupmusic.com</a>. We may ask you to verify your identity before fulfilling your request.</p>

          <h4>6.3 Correction</h4>
          <p>You may contact us to request correction of any inaccurate or incomplete Personal Information we hold about you.</p>

          <h4>6.4 Erasure / Deletion</h4>
          <p>You may request deletion of your Personal Information. We will assess your request and delete the information unless an exemption under law applies or the information is required to be preserved due to a pending claim or litigation.</p>

          <h4>6.5 Objecting to or Restricting Processing</h4>
          <p>You may contact us to object to any Processing of your Personal Information or to request a restriction of Processing. We will assess your request and determine whether we have any legal or other justification for continued Processing.</p>

          <h4>6.6 Data Portability</h4>
          <p>You may request to receive your Personal Information in a portable format, or to direct us to transfer it directly to another company where technically feasible.</p>
        </section>

        <section>
          <h3>7. Exercising Your Rights</h3>
          <p>We will respond to your requests regarding the rights outlined in Section 6 without unnecessary delay, and in any event within one month of receiving the request. If your request is complex or we are responding to many requests, we may require up to two additional months to respond. If we need the extended time, we will notify you within one month of receiving your request and provide the reason for the delay. If we cannot grant your request, we will let you know within one month and provide the reason, along with information about your right to lodge a complaint with a supervisory authority.</p>
        </section>

        <section>
          <h3>8. California Supplemental Privacy Notice</h3>
          <p>If you are a California resident, we will process your Personal Information in accordance with the California Consumer Privacy Act ("CCPA").</p>

          <h4>Your Rights</h4>
          <p>Under the CCPA, you have the right to:</p>
          <ul>
            <li>Request disclosure of the categories and specific pieces of Personal Information we have collected about you</li>
            <li>Request disclosure of the categories of sources from which Personal Information was collected</li>
            <li>Request disclosure of the business or commercial purpose for collecting your Personal Information</li>
            <li>Request disclosure of the categories of third parties with whom we share Personal Information</li>
            <li>Request deletion of your Personal Information (with exceptions)</li>
            <li>Opt out of the sale of your Personal Information (note: we do not sell Personal Information)</li>
            <li>Not be discriminated against for exercising any of the foregoing rights</li>
          </ul>

          <h4>CCPA Categories of Personal Information We Collect</h4>
          <div className="legal-table-wrapper">
            <table className="legal-table">
              <thead>
                <tr>
                  <th>PI Category</th>
                  <th>Collected</th>
                  <th>Sources</th>
                  <th>Sold</th>
                  <th>Disclosed for Business Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Identifiers (name, email, IP address, Firebase/Spotify user IDs)</td>
                  <td>Yes</td>
                  <td>Directly from you; Firebase; Google OAuth; Spotify OAuth</td>
                  <td>No</td>
                  <td>Yes — Firebase, Vercel, Render</td>
                </tr>
                <tr>
                  <td>Information protected against security breaches (passwords, account credentials)</td>
                  <td>Yes (encrypted)</td>
                  <td>Directly from you</td>
                  <td>No</td>
                  <td>No</td>
                </tr>
                <tr>
                  <td>Protected classification information (race, gender, etc.)</td>
                  <td>No</td>
                  <td>—</td>
                  <td>No</td>
                  <td>No</td>
                </tr>
                <tr>
                  <td>Commercial information</td>
                  <td>No</td>
                  <td>—</td>
                  <td>No</td>
                  <td>No</td>
                </tr>
                <tr>
                  <td>Internet/electronic activity (app usage, session data)</td>
                  <td>Yes</td>
                  <td>Firebase Analytics (automatic)</td>
                  <td>No</td>
                  <td>Yes — Firebase</td>
                </tr>
                <tr>
                  <td>Geolocation (approximate, via IP)</td>
                  <td>Yes</td>
                  <td>Firebase Analytics (automatic)</td>
                  <td>No</td>
                  <td>Yes — Firebase</td>
                </tr>
                <tr>
                  <td>Audio/video data</td>
                  <td>No</td>
                  <td>—</td>
                  <td>No</td>
                  <td>No</td>
                </tr>
                <tr>
                  <td>Professional/employment information</td>
                  <td>No</td>
                  <td>—</td>
                  <td>No</td>
                  <td>No</td>
                </tr>
                <tr>
                  <td>Education information</td>
                  <td>No</td>
                  <td>—</td>
                  <td>No</td>
                  <td>No</td>
                </tr>
                <tr>
                  <td>Biometrics</td>
                  <td>No</td>
                  <td>—</td>
                  <td>No</td>
                  <td>No</td>
                </tr>
                <tr>
                  <td>Inferences from the above</td>
                  <td>No</td>
                  <td>—</td>
                  <td>No</td>
                  <td>No</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h4>Submitting Requests</h4>
          <p>You may submit CCPA requests by emailing <a href="mailto:support@warriorwalkupmusic.com">support@warriorwalkupmusic.com</a>. Please provide your email address, first and last name, California residential address, and phone number. We will respond to verifiable requests from California consumers within forty-five (45) days from receipt as required by law.</p>

          <h4>Authorized Agent</h4>
          <p>Under the CCPA, you may designate an authorized agent to make requests on your behalf. To do so, provide us with a written and signed authorization letter that includes your name, California residential address, email, phone number, and the full name, address, and relationship to you of your authorized agent. Please send the authorization to <a href="mailto:support@warriorwalkupmusic.com">support@warriorwalkupmusic.com</a>. For fraud prevention purposes, we will send our response directly to you, not the authorized agent.</p>

          <h4>Do Not Sell My Personal Information</h4>
          <p>We do not sell your Personal Information. If our practices change, we will update this Privacy Notice and provide a mechanism to opt out as required by the CCPA.</p>
        </section>

        <section>
          <h3>9. Contact Us</h3>
          <p>If you have a complaint or concern about how we Process your Personal Information, or if you have any privacy-related questions or would like to make any requests, please contact us:</p>
          <p>
            <strong>Nerdette Ventures LLC</strong><br />
            DBA Warrior Walkup Music<br />
            Email: <a href="mailto:support@warriorwalkupmusic.com">support@warriorwalkupmusic.com</a><br />
            Website: <a href="https://warriorwalkupmusic.com" target="_blank" rel="noopener noreferrer">warriorwalkupmusic.com</a>
          </p>
        </section>

      </div>
    </div>
  );
}

export default PrivacyPolicy;
